import { type } from 'arktype';
import { createPaginatedResponseSchema } from '../shared/pagination';
import { userSearchResponseSchema } from '../users';

/** Los dos enumerados de "invitation" (documento 04, §5.2). */

export const INVITATION_TYPES = ['DIRECT_MEMBER', 'REFERRAL', 'CREATE_EXPERIENCE'] as const;
export const invitationTypeSchema = type.enumerated(...INVITATION_TYPES);
export type InvitationType = typeof invitationTypeSchema.infer;

// 'REVOKED' se añade en la iteración 04 (documento 04 §8): quien reparte un enlace puede
// revocarlo antes de que se use. Lo escribe solo el endpoint de revocación (fuera de esta
// tarea); aquí solo se declara el contrato.
export const INVITATION_STATUSES = ['PENDING', 'OPENED', 'ACCEPTED', 'DECLINED', 'EXPIRED', 'REVOKED'] as const;
export const invitationStatusSchema = type.enumerated(...INVITATION_STATUSES);
export type InvitationStatus = typeof invitationStatusSchema.infer;

/**
 * Los cinco resultados posibles de comprobar un enlace sin consumirlo (documento 04, §6).
 * Un discriminante explícito, nunca un booleano: cada desenlace narra una pantalla
 * completa distinta (las cinco puertas del documento 03b, §10.3), y `wrong-user` nunca
 * lleva ningún dato de para quién era el enlace (RN-4 del documento 04).
 */
export const REDEEM_INVITATION_RESULTS = ['ok', 'wrong-user', 'expired', 'used', 'not-found'] as const;
export const redeemInvitationResultSchema = type.enumerated(...REDEEM_INVITATION_RESULTS);
export type RedeemInvitationResult = typeof redeemInvitationResultSchema.infer;

/**
 * `GET /invitations/redeem/:token` (documento 04, §6). Esta iteración solo adelanta la
 * comprobación y el consumo transaccional al crear (decisiones.md, decisión 1); generar,
 * listar y revocar enlaces son de la iteración 4, junto con su propio contrato de entrada.
 */
export const redeemInvitationResponseSchema = type({
	resultado: redeemInvitationResultSchema,
	// Documento 02c: el grupo de WhatsApp al que lleva este enlace. Lo escribe administración al
	// generar el enlace y viaja DESCIFRADO por el backend (ADR-0019) — nunca el valor cifrado en
	// crudo. Opcional: ausente cuando el resultado no es 'ok' o el enlace no tiene grupo asignado.
	'whatsappGroupUrl?': 'string | null',
}).onUndeclaredKey('delete');

export type RedeemInvitationResponse = typeof redeemInvitationResponseSchema.infer;

/**
 * `POST /invitations` (documento 04, §10.1): generar un enlace de organizador. `expiresAt` es
 * OBLIGATORIO — no existe el enlace eterno (RN-3 del documento 04), decisión ya cerrada. El
 * resto son opcionales: `targetUserId` restringe el enlace a una persona concreta (vacío vale
 * para cualquiera con sesión); `whatsappGroupUrl`, el grupo al que se une quien lo usa
 * (CIFRADO en la base, ADR-0019 — este contrato describe la forma que cruza HTTP, en claro);
 * `plannedDate`, la fecha aproximada del plan, que alimenta los consejos de contexto del §7.2;
 * `templateSeriesId`/`templateId`, la plantilla preseleccionada del §10.1 campo 3 — dos
 * columnas nulables con exclusión mutua (una plantilla "deducida" de una serie real, o una
 * "guardada" a mano), nunca las dos a la vez, tal como ya modela la balda de plantillas de la
 * iteración 03.
 */
export const createInvitationSchema = type({
	expiresAt: 'string.date.iso',
	'plannedDate?': 'string.date.iso',
	'targetUserId?': 'string.uuid',
	'templateId?': 'string.uuid',
	'templateSeriesId?': 'string.uuid',
	'whatsappGroupUrl?': 'string',
}).onUndeclaredKey('delete');

export type CreateInvitation = typeof createInvitationSchema.infer;

/**
 * El DTO de un enlace de organizador para su propio dueño (documento 04, §10.1: la lista "mis
 * enlaces"). `token` viaja porque hace falta para componer la URL compartible; el destinatario
 * se redacta a `{ id, username, fullName }` (reutiliza `userSearchResponseSchema`) — JAMÁS
 * correo ni teléfono, es CA-04.11 aplicado también aquí, no solo al buscador de personas.
 * Deliberadamente SIN `whatsappGroupUrl`: no hace falta en un listado y así se evita cualquier
 * tentación de devolver el valor cifrado en crudo (ADR-0019) por descuido.
 *
 * `id` es OBLIGATORIO: la lista de enlaces repartidos del documento 04 §10.2 dispara dos
 * acciones por fila, `DELETE /invitations/:id` (revocar) y `GET /invitations/:id/message`
 * (volver a copiar el mensaje), y ninguna de las dos se puede invocar sin el identificador;
 * además `DELETE` responde 200 con el DTO del recurso borrado, que por convención de este
 * repositorio debe llevar su propio `id`. `experienceId` es opcional y nulable: la columna
 * "Estado" de esa misma lista es "Pendiente · caducado · usado, con la experiencia que
 * produjo" (documento 04 §10.2) — se rellena solo al consumir el enlace y es lo único que lo
 * ata a la experiencia resultante, la métrica que exige el indicador "cuántos enlaces acaban
 * en plan" de la Definition of Done del §14.
 *
 * `experienceTitle` completa esa misma columna: sin el título legible, "con la experiencia que
 * produjo" solo se puede enlazar a un identificador ciego, no leer. Un campo plano, no un
 * objeto anidado que duplicaría `experienceId` — mismo criterio que el resto de este DTO
 * redactado. Opcional y nulable por lo mismo que su hermano: ausente hasta consumir el enlace,
 * y puede seguir siendo `null` después (la experiencia fue a la papelera o su referencia quedó
 * a `null`, CL-04.5). Sin problema de privacidad al mostrarlo: un enlace solo llega a
 * consumirse cuando la experiencia nace `MEMBERS_ONLY`, así que su título ya es visible para
 * toda la comunidad desde que existe.
 *
 * `revokedAt`/`revokedById`: quién revocó el enlace y cuándo (columnas de la oleada 1,
 * `InvitationsService.revocar`) — el cliente lo pidió expresamente: «que quede registro de quién
 * revocó y cuándo, y con eso basta». Mismo criterio "ausente = sin permiso" que `whatsappGroupUrl`
 * en `redeemInvitationResponseSchema`: el backend (`invitations.mapper.ts`) solo los incluye
 * cuando quien pregunta es `ADMIN` — para cualquier otra persona no viajan, ni siquiera en su
 * propio enlace, sea o no quien lo revocó. `null` mientras el enlace no se ha revocado (hay
 * permiso, no hay dato); los dos siempre viajan juntos o ausentes juntos, nunca uno sin el otro.
 */
export const invitationResponseSchema = type({
	createdAt: 'string.date.iso',
	'destinatario?': userSearchResponseSchema,
	'experienceId?': 'string.uuid | null',
	'experienceTitle?': 'string | null',
	expiresAt: 'string.date.iso',
	id: 'string.uuid',
	'revokedAt?': 'string.date.iso | null',
	'revokedById?': 'string.uuid | null',
	status: invitationStatusSchema,
	token: 'string',
	type: invitationTypeSchema,
}).onUndeclaredKey('delete');

export type InvitationResponse = typeof invitationResponseSchema.infer;

export const invitationPaginatedResponseSchema = createPaginatedResponseSchema(invitationResponseSchema);
export type InvitationPaginatedResponse = typeof invitationPaginatedResponseSchema.infer;

/**
 * Los cinco consejos de contexto del reparto (documento 04, §7.2): cada uno resuelve una
 * regla pura sobre datos ya consultados (`rules/invitationTips.ts`) y viaja a la pantalla
 * como CLAVE i18n, nunca como texto (RNF-03, AGENTS.md) — el frontend redacta.
 */
export const CONSEJO_CONTEXTO_CLAVES = [
	'mesFlojo',
	'mesFuerteDePlantilla',
	'findeCargado',
	'primeraVez',
	'planDeRiesgo',
] as const;
export const consejoContextoClaveSchema = type.enumerated(...CONSEJO_CONTEXTO_CLAVES);
export type ConsejoContextoClave = typeof consejoContextoClaveSchema.infer;

/**
 * Un consejo de contexto: su clave más los datos PLANOS y TIPADOS que la interpolación i18n
 * necesita — nunca `unknown`, nunca texto ya redactado. `mes` (1-12) alimenta `mesFlojo` y
 * `mesFuerteDePlantilla`; `plantilla`, el nombre legible de la plantilla, solo
 * `mesFuerteDePlantilla`; `planes`, el recuento de planes ya publicados ese fin de semana,
 * solo `findeCargado`. `primeraVez` y `planDeRiesgo` no necesitan ningún dato adicional: su
 * sola presencia en la lista ya narra el consejo completo.
 */
export const consejoContextoSchema = type({
	clave: consejoContextoClaveSchema,
	'mes?': 'number.integer',
	'planes?': 'number',
	'plantilla?': 'string',
}).onUndeclaredKey('delete');

export type ConsejoContexto = typeof consejoContextoSchema.infer;

/**
 * `GET /invitations/:id/message` (documento 04, §7): el mensaje listo para compartir tras
 * generar un enlace. `enlace` es la URL completa ya compuesta (el backend la arma, el
 * frontend no reconstruye nada); `consejos`, los de contexto de arriba; `recordatoriosSeguridad`
 * son CLAVES i18n del namespace `safetyTips` (`contracts/safetyTips`), no frases — la API
 * NUNCA devuelve texto humano (RNF-03).
 */
export const invitationMessageResponseSchema = type({
	consejos: consejoContextoSchema.array(),
	enlace: 'string',
	recordatoriosSeguridad: 'string[]',
}).onUndeclaredKey('delete');

export type InvitationMessageResponse = typeof invitationMessageResponseSchema.infer;
